import React, { useEffect } from "react";
import { useOnboardSuccessQuery, useGetProfileInformationQuery } from "../../../Redux/apis/ApiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const accountId = queryParams.get("accountId");
  const hasToken = !!localStorage.getItem("authToken");

  // Always call hooks at the top level
  const { data, error, isLoading } = useOnboardSuccessQuery(accountId, { skip: !hasToken || !accountId });
  const { refetch: refetchProfile } = useGetProfileInformationQuery();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!hasToken) {
      navigate(
        "/login?redirect=" + encodeURIComponent(location.pathname + location.search)
      );
    }
  }, [hasToken, location, navigate]);

  // Refetch profile after onboarding update
  useEffect(() => {
    if (data) {
      refetchProfile();
    }
  }, [data, refetchProfile]);

  // Error message helper
  const getErrorMessage = (err) => {
    if (!err) return null;
    if (err.status === 401) return "You must be logged in to complete onboarding.";
    if (err.status === 409) return err.data?.message || "Account already linked to another Stripe account.";
    if (err.status === 400) return err.data?.message || "Invalid Stripe account information.";
    if (err.status === 404) return err.data?.message || "User or Stripe account not found.";
    if (err.status === 500) return "A server error occurred. Please try again or contact support.";
    return err.data?.message || err.message || "An unknown error occurred. Please try again or contact support.";
  };

  // If not authenticated, show nothing (redirect handled by useEffect)
  if (!hasToken) return null;

  // If accountId is missing, show error and back button
  if (!accountId) {
    return (
      <Container>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography color="error" sx={{ mt: 4 }}>
            Missing Stripe account information. Please try connecting again.
          </Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate("/settings")}>Back to Settings</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <h1 style={{ color: "#061F44" }}>Stripe Onboarding Status</h1>
        {isLoading && (
          <Typography variant="h5" gutterBottom>
            Loading...
          </Typography>
        )}
        {error && (
          <>
            <Typography variant="h5" gutterBottom color="error">
              {getErrorMessage(error)}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => navigate("/settings")}
            >
              Back to Settings
            </Button>
          </>
        )}
        {data && (
          <>
            <Typography variant="h5" gutterBottom>
              {data.message}
            </Typography>
            {data.status === "incomplete" && (
              <Button
                variant="contained"
                color="warning"
                sx={{ mt: 2 }}
                onClick={() => navigate("/settings")}
              >
                Resume Onboarding
              </Button>
            )}
            {data.status === "success" && (
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default Success;
